import type { ContactContextField, EnrichmentBlock } from '@/types/workflow'

/**
 * SINGLE SOURCE OF TRUTH for enrichment fields across the workflow builder.
 *
 * Three surfaces used to keep their own hand-maintained lists and drifted apart
 * (a provider could be enrichable with no matching Send Email field, or a field
 * could show with no producing provider — see AB#21980):
 *   - workflow-utils.getUpstreamEnrichmentFields   which fields an upstream
 *     Enrich Contacts node makes available downstream
 *   - ContactFieldSelector                         the personalization checkboxes + labels
 *   - NodeConfigPanel "Available Enrichment Data"  the chips + their count
 *
 * They now all derive from ENRICHMENT_GROUPS below, so adding or changing an
 * enrichment source is a one-line edit that stays in sync everywhere.
 *
 * The provider names here MUST match the backend enrichment registry
 * (app/services/enrichment/orchestrator.py PROVIDER_METADATA), and the `block`
 * values MUST match the native AI blocks (EnrichmentBlock). That backend
 * registry is the contract this mirrors.
 */

export interface EnrichmentFieldDef {
  key: ContactContextField
  /** Compact label for chips (NodeConfigPanel "Available Enrichment Data"). */
  shortLabel: string
  /** Descriptive label for the ContactFieldSelector rows. */
  label: string
  /** For native AI fields: the enabled_blocks entry that populates this field. */
  block?: EnrichmentBlock
}

export interface EnrichmentGroupDef {
  /** Group header shown in ContactFieldSelector. */
  name: string
  /** Enrich Contacts provider name(s) whose selection makes these fields
   *  available. Omitted for the native AI group (driven by enabled_blocks). */
  providers?: string[]
  /** Custom AI is rendered as recipe-driven groups in ContactFieldSelector, so
   *  its static field is not listed there; the block -> field mapping still
   *  applies for upstream availability. */
  recipeDriven?: boolean
  fields: EnrichmentFieldDef[]
}

export const ENRICHMENT_GROUPS: EnrichmentGroupDef[] = [
  {
    name: 'AI Enrichment',
    fields: [
      { key: 'outreach_hooks',      block: 'outreach_hooks',      shortLabel: 'Outreach Hooks',      label: 'Outreach Hooks' },
      { key: 'buying_signals',      block: 'buying_signals',      shortLabel: 'Buying Signals',      label: 'Buying Signals' },
      { key: 'communication_prefs', block: 'communication_prefs', shortLabel: 'Communication Prefs', label: 'Communication Prefs' },
      { key: 'competitive_intel',   block: 'competitive_intel',   shortLabel: 'Competitive Intel',   label: 'Competitive Intel' },
      { key: 'relationship_map',    block: 'relationship_map',    shortLabel: 'Relationship Map',    label: 'Relationship Map' },
      { key: 'content_relevance',   block: 'content_relevance',   shortLabel: 'Content Relevance',   label: 'Content Relevance' },
      { key: 'person_enrichment',   block: 'person',              shortLabel: 'Person (AI)',         label: 'Person' },
      { key: 'company_enrichment',  block: 'company',             shortLabel: 'Company (AI)',        label: 'Company' },
      { key: 'sector_enrichment',   block: 'sector',              shortLabel: 'Sector (AI)',         label: 'Sector & Industry' },
      { key: 'economy_enrichment',  block: 'economy',             shortLabel: 'Economy (AI)',        label: 'Economy & Market' },
    ],
  },
  {
    name: 'Custom AI',
    recipeDriven: true,
    fields: [
      { key: 'custom_ai_enrichment', block: 'custom_ai', shortLabel: 'Custom AI', label: 'Custom AI Result' },
    ],
  },
  {
    name: 'People Data Labs',
    providers: ['peopledatalabs'],
    fields: [
      { key: 'pdl_person',  shortLabel: 'PDL Person',  label: 'Person (title, email, phone, seniority, experience, skills, education)' },
      { key: 'pdl_company', shortLabel: 'PDL Company', label: 'Company (industry, size, funding, revenue, tech stack)' },
    ],
  },
  {
    name: 'Explorium',
    providers: ['explorium'],
    fields: [
      { key: 'explorium_person',  shortLabel: 'Explorium Person',  label: 'Person (profile, contacts, experience, skills, education)' },
      { key: 'explorium_company', shortLabel: 'Explorium Company', label: 'Company (firmographics, NAICS, SIC, revenue, employees)' },
    ],
  },
  {
    name: 'Financial Modeling Prep',
    providers: ['fmp'],
    fields: [
      { key: 'fmp_financials', shortLabel: 'FMP Financials', label: 'Financials (income statement, balance sheet, cash flow, stock metrics)' },
    ],
  },
  {
    name: 'Alpha Vantage',
    providers: ['alpha_vantage'],
    fields: [
      { key: 'alpha_vantage_financials', shortLabel: 'Alpha Vantage Financials', label: 'Financials (income statement, balance sheet, earnings, overview)' },
    ],
  },
  {
    name: 'SEC EDGAR',
    providers: ['sec_edgar'],
    fields: [
      { key: 'sec_edgar_filings', shortLabel: 'SEC EDGAR Filings', label: 'Filings (financial statements, 10-K, 10-Q, 8-K filings)' },
    ],
  },
  // ---- Parked for a future scope, not deleted -------------------------
  // RocketReach, Lusha, Cognism, UpLead and Snov.io, commented out to match
  // orchestrator.PROVIDER_METADATA, which parked them because Settings >
  // Integrations has no field for their API keys. This file's contract, stated
  // at the top, is that its provider names mirror that registry -- leaving
  // them here would offer personalization fields for data no configured
  // provider can produce, which is the exact drift AB#21980 introduced this
  // file to stop.
  //
  // Their ContactContextField keys are deliberately LEFT in types/workflow.ts.
  // A workflow saved earlier may still name one, and keeping the union member
  // means such a value stays type-valid; it simply has no group to render.
  // Uncomment these alongside the backend blocks.
  // {
  //   name: 'RocketReach',
  //   providers: ['rocketreach'],
  //   fields: [
  //     { key: 'rocketreach_person', shortLabel: 'RocketReach Person', label: 'Person (email, phone, title, employer, LinkedIn)' },
  //   ],
  // },
  // {
  //   name: 'Lusha',
  //   providers: ['lusha'],
  //   fields: [
  //     { key: 'lusha_person',  shortLabel: 'Lusha Person',  label: 'Person (email, phone, title, company)' },
  //     { key: 'lusha_company', shortLabel: 'Lusha Company', label: 'Company (industry, size, revenue, location)' },
  //   ],
  // },
  // {
  //   name: 'Cognism',
  //   providers: ['cognism'],
  //   fields: [
  //     { key: 'cognism_person',  shortLabel: 'Cognism Person',  label: 'Person (email, phone, title, seniority, company)' },
  //     { key: 'cognism_company', shortLabel: 'Cognism Company', label: 'Company (industry, employees, revenue, tech stack)' },
  //   ],
  // },
  // {
  //   name: 'UpLead',
  //   providers: ['uplead'],
  //   fields: [
  //     { key: 'uplead_person', shortLabel: 'UpLead Person', label: 'Person (email, phone, title, company, industry)' },
  //   ],
  // },
  // {
  //   name: 'Snov.io',
  //   providers: ['snov'],
  //   fields: [
  //     { key: 'snov_email', shortLabel: 'Snov.io Email', label: 'Email (find email, verify email)' },
  //   ],
  // },
  // ---- end parked ------------------------------------------------------

  {
    name: 'News',
    providers: ['news'],
    fields: [
      { key: 'news_enrichment', shortLabel: 'News', label: 'News (company, person, and industry articles)' },
    ],
  },
  // NOTE: the Apollo and Hunter providers (orchestrator PROVIDER_METADATA) have
  // no dedicated contact field — their person/company data is merged into the
  // native Person/Company enrichment (see enrichment/merge.py), so they
  // intentionally do not contribute their own Send Email personalization field.
]

/** block -> the ContactContextField it populates (native AI enrichment). */
export const ENRICHMENT_BLOCK_TO_FIELD: Partial<Record<EnrichmentBlock, ContactContextField>> =
  ENRICHMENT_GROUPS.reduce((acc, g) => {
    for (const f of g.fields) if (f.block) acc[f.block] = f.key
    return acc
  }, {} as Partial<Record<EnrichmentBlock, ContactContextField>>)

/** Enrich Contacts provider name -> the ContactContextFields it populates. */
export const PROVIDER_FIELD_MAP: Record<string, ContactContextField[]> =
  ENRICHMENT_GROUPS.reduce((acc, g) => {
    for (const p of g.providers || []) {
      acc[p] = [...(acc[p] || []), ...g.fields.map(f => f.key)]
    }
    return acc
  }, {} as Record<string, ContactContextField[]>)

/** field key -> compact label for chips. */
export const ENRICHMENT_FIELD_SHORT_LABELS: Record<string, string> =
  ENRICHMENT_GROUPS.reduce((acc, g) => {
    for (const f of g.fields) acc[f.key] = f.shortLabel
    return acc
  }, {} as Record<string, string>)

/** Static (non recipe-driven) enrichment groups for the ContactFieldSelector. */
export const ENRICHMENT_SELECTOR_GROUPS = ENRICHMENT_GROUPS.filter(g => !g.recipeDriven)
